import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

interface LibraryContextValue {
  isLibrarySidebarOpen: boolean;
  setLibrarySidebarOpen: (open: boolean) => void;
  toggleLibrarySidebar: () => void;
  
  isInspectorOpen: boolean;
  setInspectorOpen: (open: boolean) => void;
  toggleInspector: () => void;
  
  selectedDocumentId: string | null;
  setSelectedDocumentId: (id: string | null) => void;
  /** When opening a document from a mastermind space, set this so shared-space edits use the correct RPC. */
  documentEditSpaceContextId: string | null;
  setDocumentEditSpaceContextId: (id: string | null) => void;
  selectedReferenceId: string | null;
  setSelectedReferenceId: (id: string | null) => void;

  selectedFolderId: string | null;
  setSelectedFolderId: (id: string | null) => void;
  
  expandedFolderIds: Set<string>;
  toggleFolderExpanded: (id: string) => void;
  setExpandedFolderIds: (ids: Set<string>) => void;
  
  draggedItemId: string | null;
  draggedItemType: "document" | "folder" | null;
  setDraggedItem: (id: string | null, type: "document" | "folder" | null) => void;
}

const LibraryContext = createContext<LibraryContextValue | undefined>(undefined);

interface LibraryProviderProps {
  children: ReactNode;
}

export function LibraryProvider({ children }: LibraryProviderProps) {
  const [isLibrarySidebarOpen, setLibrarySidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("library_sidebar_open");
      return saved !== "false";
    }
    return true;
  });

  const [isInspectorOpen, setInspectorOpen] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("library_inspector_open");
      return saved === "true";
    }
    return false;
  });

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [documentEditSpaceContextId, setDocumentEditSpaceContextId] = useState<string | null>(null);
  const [selectedReferenceId, setSelectedReferenceId] = useState<string | null>(null);

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set(["root"]));
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [draggedItemType, setDraggedItemType] = useState<"document" | "folder" | null>(null);

  const toggleLibrarySidebar = useCallback(() => {
    setLibrarySidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem("library_sidebar_open", String(next));
      return next;
    });
  }, []);

  const handleSetLibrarySidebarOpen = useCallback((open: boolean) => {
    setLibrarySidebarOpen(open);
    localStorage.setItem("library_sidebar_open", String(open));
  }, []);

  const toggleInspector = useCallback(() => {
    setInspectorOpen((prev) => {
      const next = !prev;
      localStorage.setItem("library_inspector_open", String(next));
      return next;
    });
  }, []);

  const handleSetInspectorOpen = useCallback((open: boolean) => {
    setInspectorOpen(open);
    localStorage.setItem("library_inspector_open", String(open));
  }, []);

  const toggleFolderExpanded = useCallback((id: string) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const setDraggedItem = useCallback((id: string | null, type: "document" | "folder" | null) => {
    setDraggedItemId(id);
    setDraggedItemType(type);
  }, []);

  const value: LibraryContextValue = {
    isLibrarySidebarOpen,
    setLibrarySidebarOpen: handleSetLibrarySidebarOpen,
    toggleLibrarySidebar,
    isInspectorOpen,
    setInspectorOpen: handleSetInspectorOpen,
    toggleInspector,
    selectedDocumentId,
    setSelectedDocumentId,
    documentEditSpaceContextId,
    setDocumentEditSpaceContextId,
    selectedReferenceId,
    setSelectedReferenceId,
    selectedFolderId,
    setSelectedFolderId,
    expandedFolderIds,
    toggleFolderExpanded,
    setExpandedFolderIds,
    draggedItemId,
    draggedItemType,
    setDraggedItem,
  };

  return (
    <LibraryContext.Provider value={value}>
      {children}
    </LibraryContext.Provider>
  );
}

export function useLibrary() {
  const context = useContext(LibraryContext);
  if (context === undefined) {
    throw new Error("useLibrary must be used within a LibraryProvider");
  }
  return context;
}

export function useLibraryOptional() {
  return useContext(LibraryContext);
}
